import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import { listStaffResult } from '@/modules/staff/services';
import {
  createProposal,
  deleteProposal as deleteProposalMutation,
  getDealProposalsStaticConfig,
  listProposalsResult,
  promoteProposalToInProgress,
  saveProposal,
  updateProposalStage,
} from '@/modules/deals/services';
import {
  calculateProposalStats,
  filterProposals,
  getDefaultProposalFilters,
  hasActiveProposalFilters,
} from '@/modules/deals/utils';
import { filterProposalsByOverview } from '../../utils/proposals/proposals.helpers';

const {
  proposalStages,
} = getDealProposalsStaticConfig();

function normalizeClientId(clientId) {
  if (clientId === null || clientId === undefined || clientId === '') {
    return '';
  }

  return String(clientId);
}

function getClientDisplayName(client) {
  return client?.company || client?.businessName || client?.name || '';
}

function buildClientsMap(clients) {
  return new Map(
    (clients || [])
      .map((client) => [normalizeClientId(client?.id), client])
      .filter(([clientId]) => clientId)
  );
}

function resolveClientReference({ clientId, legacyClientName, clientsMap }) {
  const normalizedClientId = normalizeClientId(clientId);
  const legacyName = String(legacyClientName || '').trim();

  if (!normalizedClientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
      clientLegacyName: legacyName,
    };
  }

  const client = clientsMap.get(normalizedClientId);
  if (client) {
    return {
      clientResolvedName:
        getClientDisplayName(client) || legacyName || `Cliente ${normalizedClientId}`,
      clientResolutionStatus: 'resolved',
      clientLegacyName: legacyName,
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientResolutionStatus: 'unresolved_client_reference',
    clientLegacyName: legacyName,
  };
}

function enrichProposalsWithClientResolution(proposals, clients) {
  const clientsMap = buildClientsMap(clients);

  return (proposals || []).map((proposal) => {
    const resolution = resolveClientReference({
      clientId: proposal.clientId,
      legacyClientName: proposal.client,
      clientsMap,
    });

    return {
      ...proposal,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

function enrichProposalWithClientResolution(proposal, clients) {
  return enrichProposalsWithClientResolution([proposal], clients)[0];
}

export function useProposalsData() {
  const { logout } = useSession();
  const [searchParams] = useSearchParams();

  const [proposals, setProposals] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [isLoadingProposals, setIsLoadingProposals] = useState(true);
  const [proposalsModuleDisabled, setProposalsModuleDisabled] = useState(false);
  const [proposalsUnavailable, setProposalsUnavailable] = useState(false);
  const [proposalsError, setProposalsError] = useState('');
  const [proposalsAuthRequired, setProposalsAuthRequired] = useState(false);
  const [proposalsTenantDatabaseBlocked, setProposalsTenantDatabaseBlocked] = useState(false);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState(getDefaultProposalFilters());
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);
  const [promotionStates, setPromotionStates] = useState({});
  const createProposalPromiseRef = useRef(null);
  const promotionLocksRef = useRef(new Set());
  const promotionPromisesRef = useRef(new Map());
  const selectedProposalRef = useRef(null);
  const clientFilterParam = normalizeClientId(searchParams.get('clientId'));

  const baseScopedProposals = useMemo(() => {
    return filterProposals(proposals, {
      ...getDefaultProposalFilters(),
      clientFilter: filters.clientFilter,
    });
  }, [proposals, filters.clientFilter]);

  const filteredProposalsBeforeOverview = useMemo(() => {
    return filterProposals(proposals, filters);
  }, [proposals, filters]);

  const filteredProposals = useMemo(() => {
    return filterProposalsByOverview(filteredProposalsBeforeOverview, activeOverviewFilter);
  }, [activeOverviewFilter, filteredProposalsBeforeOverview]);

  const stats = useMemo(() => {
    return calculateProposalStats(baseScopedProposals, proposalStages);
  }, [baseScopedProposals, proposalStages]);

  const assigneeOptions = useMemo(() => {
    const proposalAssignees = proposals
      .map((proposal) => proposal.assignee)
      .filter(Boolean);

    const staffNames = availableStaff.map((member) => member.name).filter(Boolean);

    return [...new Set([...proposalAssignees, ...staffNames])].sort((a, b) =>
      a.localeCompare(b, 'it-IT')
    );
  }, [availableStaff, proposals]);

  const hasActiveFiltersValue = useMemo(() => {
    return hasActiveProposalFilters(filters) || Boolean(activeOverviewFilter);
  }, [activeOverviewFilter, filters]);

  useEffect(() => {
    setFilters((prev) => {
      const nextClientFilter = clientFilterParam || 'all';
      if (prev.clientFilter === nextClientFilter) return prev;
      return {
        ...prev,
        clientFilter: nextClientFilter,
      };
    });
  }, [clientFilterParam]);

  useEffect(() => {
    let cancelled = false;

    async function loadProposalsContext() {
      const [data, clientsResult, staffResult] = await Promise.all([
        listProposalsResult(),
        listClientsResult(),
        listStaffResult(),
      ]);

      if (!cancelled) {
        const clients = clientsResult.ok ? clientsResult.data : [];
        const moduleDisabled = data.error === 'DEALS_MODULE_DISABLED';
        const tenantDatabaseBlocked =
          data.error === 'DEALS_TENANT_DATABASE_NOT_READY' ||
          data.error === 'DEALS_TENANT_DATABASE_METADATA_MISSING' ||
          data.error === 'DEALS_TENANT_DATABASE_UNAVAILABLE';
        const authRequired =
          data.error === 'PROPOSALS_AUTH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_AUTH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_REFRESH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_EXPIRED' ||
          staffResult.error === 'STAFF_AUTH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_REFRESH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_EXPIRED';
        if (authRequired) {
          logout();
        }

        setProposals(data.ok ? enrichProposalsWithClientResolution(data.data, clients) : []);
        setAvailableClients(clients);
        setAvailableStaff(staffResult.ok ? staffResult.data : []);
        setIsLoadingProposals(false);
        setProposalsModuleDisabled(moduleDisabled);
        setProposalsUnavailable(!data.ok && data.error !== 'PROPOSALS_AUTH_REQUIRED' && !moduleDisabled && !tenantDatabaseBlocked);
        setProposalsAuthRequired(data.error === 'PROPOSALS_AUTH_REQUIRED');
        setProposalsTenantDatabaseBlocked(tenantDatabaseBlocked);
        setProposalsError(data.ok ? '' : data.message || data.error || '');
        setClientsUnavailable(!clientsResult.ok);
        setClientsError(clientsResult.ok ? '' : clientsResult.error || '');
        setStaffUnavailable(!staffResult.ok);
        setStaffError(staffResult.ok ? '' : staffResult.message || staffResult.error || '');
      }
    }

    loadProposalsContext();

    return () => {
      cancelled = true;
    };
  }, []);

  function openProposalDetails(proposal) {
    selectedProposalRef.current = proposal;
    setSelectedProposal(proposal);
    setIsDetailDialogOpen(true);
  }

  function closeProposalDetails() {
    selectedProposalRef.current = null;
    setIsDetailDialogOpen(false);
    setSelectedProposal(null);
  }

  function openCreateDialog() {
    setIsCreateDialogOpen(true);
  }

  function closeCreateDialog() {
    setIsCreateDialogOpen(false);
  }

  function handleCreateProposal(newProposal) {
    if (createProposalPromiseRef.current) {
      return createProposalPromiseRef.current;
    }

    const request = (async () => {
      try {
        const result = await createProposal(newProposal);
        if (!result.ok) return result;

        const proposal = enrichProposalWithClientResolution(result.data.proposal, availableClients);

        setProposals((prev) =>
          [proposal, ...prev.filter((item) => item.id !== proposal.id)].sort((left, right) =>
            String(right.proposalDate || '').localeCompare(String(left.proposalDate || ''))
          )
        );
        setIsCreateDialogOpen(false);
        return result;
      } finally {
        createProposalPromiseRef.current = null;
      }
    })();

    createProposalPromiseRef.current = request;
    return request;
  }

  async function handleSaveProposal(updatedProposal) {
    const result = await saveProposal(updatedProposal);
    if (!result.ok) return result;

    const proposal = enrichProposalWithClientResolution(result.data.proposal, availableClients);

    setProposals((prev) =>
      prev.map((item) => (item.id === proposal.id ? proposal : item))
    );
    selectedProposalRef.current = proposal;
    setSelectedProposal(proposal);
    return result;
  }

  async function handleDeleteProposal(proposalId) {
    const result = await deleteProposalMutation(proposalId);
    if (!result.ok) return result;

    setProposals((prev) => prev.filter((item) => item.id !== proposalId));
    if (selectedProposalRef.current?.id === proposalId) {
      closeProposalDetails();
    }
    return result;
  }

  function handlePromoteProposal(proposalId) {
    const lockKey = String(proposalId);
    if (promotionLocksRef.current.has(lockKey)) {
      return promotionPromisesRef.current.get(lockKey);
    }

    promotionLocksRef.current.add(lockKey);
    setPromotionStates((prev) => ({
      ...prev,
      [lockKey]: { loading: true, error: '' },
    }));

    const request = (async () => {
      const result = await promoteProposalToInProgress(proposalId);
      const isConvergedReplay = result.error === 'PROPOSAL_ALREADY_PROMOTED';

      if (!result.ok && !isConvergedReplay) {
        promotionLocksRef.current.delete(lockKey);
        setPromotionStates((prev) => ({
          ...prev,
          [lockKey]: {
            loading: false,
            error: result.message || 'Promozione non riuscita.',
          },
        }));
        return result;
      }

      setProposals((prev) => prev.filter((proposal) => proposal.id !== proposalId));
      setPromotionStates((prev) => ({
        ...prev,
        [lockKey]: { loading: false, error: '' },
      }));

      if (selectedProposalRef.current?.id === proposalId) {
        selectedProposalRef.current = null;
        setSelectedProposal(null);
        setIsDetailDialogOpen(false);
      }

      return isConvergedReplay
        ? {
            ...result,
            ok: true,
            replayed: true,
            error: null,
            message: '',
          }
        : result;
    })();
    promotionPromisesRef.current.set(lockKey, request);
    request.finally(() => {
      promotionPromisesRef.current.delete(lockKey);
      if (!promotionLocksRef.current.has(lockKey)) return;
      promotionLocksRef.current.delete(lockKey);
    });
    return request;
  }

  async function onDragEnd(result) {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const proposalId = Number(draggableId);
    const newStage = destination.droppableId;
    const mutationResult = await updateProposalStage(proposalId, newStage);

    if (!mutationResult.ok) return;

    const proposal = enrichProposalWithClientResolution(mutationResult.data.proposal, availableClients);

    setProposals((prev) =>
      prev.map((item) => (item.id === proposalId ? proposal : item))
    );

    if (selectedProposalRef.current?.id === proposalId) {
      selectedProposalRef.current = proposal;
      setSelectedProposal(proposal);
    }
  }

  function setSearchTerm(value) {
    setFilters((prev) => ({ ...prev, searchTerm: value }));
  }

  function setPriorityFilter(value) {
    setFilters((prev) => ({ ...prev, priorityFilter: value }));
  }

  function setAssigneeFilter(value) {
    setFilters((prev) => ({ ...prev, assigneeFilter: value }));
  }

  function setStageFilter(value) {
    setFilters((prev) => ({ ...prev, stageFilter: value }));
  }

  function setClientFilter(value) {
    setFilters((prev) => ({ ...prev, clientFilter: clientFilterParam || value || 'all' }));
  }

  function clearFilters() {
    setFilters({
      ...getDefaultProposalFilters(),
      clientFilter: clientFilterParam || 'all',
    });
    setActiveOverviewFilter(null);
  }

  function setOverviewFilter(filterKey) {
    setActiveOverviewFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      setFilters({
        ...getDefaultProposalFilters(),
        clientFilter: clientFilterParam || 'all',
      });
      return next;
    });
  }

  const activeClientFilterLabel = useMemo(() => {
    if (!filters.clientFilter || filters.clientFilter === 'all') return '';
    const client = availableClients.find(
      (item) => normalizeClientId(item.id) === normalizeClientId(filters.clientFilter)
    );

    return getClientDisplayName(client) || `Cliente ${filters.clientFilter}`;
  }, [availableClients, filters.clientFilter]);

  return {
    proposals,
    isLoadingProposals,
    proposalsModuleDisabled,
    proposalsUnavailable,
    proposalsError,
    proposalsAuthRequired,
    proposalsTenantDatabaseBlocked,
    filteredProposals,
    proposalStages,
    staff: availableStaff,
    staffUnavailable,
    staffError,
    staffCatalogStatus: staffUnavailable ? 'unavailable' : availableStaff.length > 0 ? 'available' : 'empty',
    clients: availableClients,
    clientsUnavailable,
    clientsError,
    stats,
    filters,
    activeClientFilterLabel,
    activeOverviewFilter,
    selectedProposal,
    isDetailDialogOpen,
    isCreateDialogOpen,
    promotionStates,
    assigneeOptions,
    hasActiveFilters: hasActiveFiltersValue,
    openProposalDetails,
    closeProposalDetails,
    openCreateDialog,
    closeCreateDialog,
    handleCreateProposal,
    handleSaveProposal,
    handleDeleteProposal,
    handlePromoteProposal,
    onDragEnd,
    setSearchTerm,
    setPriorityFilter,
    setAssigneeFilter,
    setStageFilter,
    setClientFilter,
    setOverviewFilter,
    clearFilters,
  };
}
